/**
 * TermsOfService — public /terms page (PR #33).
 *
 * Covers acceptable use of the Solara online portal — DISTINCT from the
 * mandate / engagement terms a client signs to engage Solara as their
 * capital advisor (those live in the executed Capital Advisory Mandate).
 *
 * **REVIEW WITH COUNSEL BEFORE CUTOVER.** Starter terms below cover
 * portal access, account responsibilities, prohibited uses, IP, disclaimer,
 * limitation of liability, and governing law (NSW). Tighten as needed.
 */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { FileText } from "lucide-react";

const LOGO_HORIZONTAL = "/logos/solara-horizontal.png";
const LAST_UPDATED = "8 May 2026";

export default function TermsOfService() {
  useEffect(() => {
    document.title = "Terms of Service — Solara Global Capital Partners";
  }, []);

  return (
    <div className="min-h-screen bg-[#fafaf9] flex flex-col">
      <header className="bg-white border-b border-gray-200 py-4">
        <div className="max-w-4xl mx-auto px-4 flex items-center justify-between">
          <Link to="/" className="block">
            <img src={LOGO_HORIZONTAL} alt="Solara Global Capital Partners" className="h-9" />
          </Link>
          <nav className="flex items-center gap-4 text-sm">
            <Link to="/privacy" className="text-gray-600 hover:text-[#bd9a59]">Privacy</Link>
            <Link to="/" className="text-gray-600 hover:text-[#bd9a59]">Home</Link>
          </nav>
        </div>
      </header>

      <main className="flex-1 max-w-4xl w-full mx-auto px-4 py-10 sm:py-12">
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 sm:p-10">
          <div className="flex items-center gap-3 mb-2">
            <FileText className="w-6 h-6 text-[#bd9a59]" />
            <h1 className="text-3xl font-semibold text-[#021d49]">Terms of Service</h1>
          </div>
          <p className="text-sm text-gray-500 mb-8">Last updated: {LAST_UPDATED}</p>

          <Section title="1. Scope">
            <p>
              These Terms of Service govern your use of the Solara Global online portal at{" "}
              <strong>app.solaraglobal.au</strong> ("the Portal"). They are separate from any Capital Advisory Mandate or other
              commercial agreement you may execute with Solara. By accessing or using the Portal you accept these terms.
            </p>
          </Section>

          <Section title="2. Account responsibilities">
            <ul className="list-disc pl-6 space-y-1">
              <li>You are responsible for keeping your login credentials confidential and for all activity under your account.</li>
              <li>You must notify us immediately at <a href="mailto:loans@sgcap.com.au" className="text-[#bd9a59] hover:underline">loans@sgcap.com.au</a> if you suspect unauthorised access.</li>
              <li>Information you provide must be accurate and not misleading. Submitting false information may be a criminal offence.</li>
              <li>You may not share access to your account, or use the Portal on behalf of any person other than yourself or an entity you are authorised to represent.</li>
            </ul>
          </Section>

          <Section title="3. Acceptable use">
            <p>You must not:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Use the Portal to upload viruses, malware, or any content you have no right to share.</li>
              <li>Attempt to access accounts, applications, or data that don't belong to you.</li>
              <li>Reverse engineer, scrape, or attempt to bypass the Portal's security or rate limits.</li>
              <li>Use the Portal in any way that could damage Solara's reputation, infrastructure, or other clients' use of it.</li>
            </ul>
          </Section>

          <Section title="4. Electronic signatures">
            <p>
              The Portal facilitates electronic signing of mandate agreements and other commercial documents under the Australian{" "}
              <em>Electronic Transactions Act 1999</em> (Cth) and the equivalent state-based legislation. By using the e-signature
              flow you consent to receiving electronic communications and to your typed name and signature image being treated as
              your legal signature on the relevant document.
            </p>
            <p>
              Each signed document includes an audit trail capturing the time, IP address, browser, and document hash at the time
              of signing. You can request a copy of any document you have signed at any time.
            </p>
          </Section>

          <Section title="5. Documents and uploads">
            <p>
              Files you upload to the Portal are stored in Solara's Google Workspace tenant and used only for the purpose of
              progressing your loan application or meeting our regulatory obligations. We retain documents in accordance with our{" "}
              <Link to="/privacy" className="text-[#bd9a59] hover:underline">Privacy Policy</Link>.
            </p>
            <p>
              Maximum upload size is 250 MB per file. We reserve the right to reject files that exceed this limit or appear to be
              corrupted, executable, or otherwise unsuitable.
            </p>
          </Section>

          <Section title="6. Intellectual property">
            <p>
              All software, design, content, and trade marks on the Portal are owned by or licensed to Solara Global Capital Pty
              Ltd. You may use the Portal solely for the purpose of progressing your engagement with Solara. No other licence is
              granted.
            </p>
            <p>
              Any document templates, term sheets, or invoice formats generated through the Portal remain the intellectual property
              of Solara, except for the specific factual data you provide (which remains yours).
            </p>
          </Section>

          <Section title="7. Disclaimer">
            <p>
              The Portal is provided on an "as is" and "as available" basis. To the extent permitted by law, Solara does not warrant
              that the Portal will be uninterrupted, error-free, or free of viruses. Solara is not a lender and does not guarantee
              that any application will result in a funding offer.
            </p>
            <p>
              Nothing on the Portal constitutes legal, taxation, or financial advice. You should obtain independent professional
              advice before making any decision in reliance on information provided through the Portal.
            </p>
          </Section>

          <Section title="8. Limitation of liability">
            <p>
              To the maximum extent permitted by law, Solara's total aggregate liability arising out of or in connection with your
              use of the Portal is limited to the fees actually received by Solara from you in the twelve months preceding the
              event giving rise to liability.
            </p>
            <p>
              Solara is not liable for indirect, consequential, or economic loss, including loss of profit, opportunity, or data.
            </p>
          </Section>

          <Section title="9. Termination">
            <p>
              You may stop using the Portal at any time. Solara may suspend or terminate your access if you breach these terms,
              if your engagement with Solara ends, or if Solara is required to do so by law. Termination does not affect any
              accrued obligations of either party.
            </p>
          </Section>

          <Section title="10. Governing law">
            <p>
              These terms are governed by the laws of New South Wales, Australia. The parties submit to the exclusive jurisdiction
              of the courts of New South Wales.
            </p>
          </Section>

          <Section title="11. Changes">
            <p>
              We may update these terms from time to time. The "Last updated" date at the top of this page reflects the most recent
              revision. Continued use of the Portal after a change constitutes acceptance of the updated terms.
            </p>
          </Section>

          <Section title="12. Contact">
            <p>
              For questions about these terms, contact us at{" "}
              <a href="mailto:loans@sgcap.com.au" className="text-[#bd9a59] hover:underline">loans@sgcap.com.au</a>.
            </p>
          </Section>
        </div>
      </main>

      <footer className="py-6 text-center text-xs text-gray-400">
        Solara Global Capital Partners · sgcap.com.au · <Link to="/privacy" className="hover:text-[#bd9a59]">Privacy</Link>
      </footer>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-8 last:mb-0">
      <h2 className="text-lg font-semibold text-[#021d49] mb-3 border-b border-gray-100 pb-2">{title}</h2>
      <div className="space-y-3 text-sm text-gray-700 leading-relaxed">{children}</div>
    </section>
  );
}
